/**
 * การวางผังต้นไม้ — ใช้ร่วมกันทั้งผังหน่วยงานและผังคน
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องแยกออกมาเป็นไฟล์กลาง
 * ────────────────────────────────────────────────────────────────
 *
 * เดิมอัลกอริทึมนี้อยู่ใน buildGraph() ของ OrgFlow.tsx ซึ่งผูกกับ OrgNode โดยตรง
 * พอต้องมีผังคนระดับ 9 ขึ้นไปซึ่งใช้โหนดคนละชนิด ทางเลือกมีสองทาง
 *
 *   copy ไปอีกชุด   ได้เร็ว แต่วันหนึ่งแก้บั๊กการวางจะแก้ที่เดียวแล้วอีกผังยังพังอยู่
 *                   โดยไม่มีใครรู้จนกว่าจะมีคนเปิดผังนั้น — เป็นกับดักที่หัวข้อ
 *                   "ขนาดและการวาง" ใน OrgFlow.tsx เขียนเตือนไว้เองตั้งแต่ตอนเพิ่มทิศที่สอง
 *   แยกออกมา       ต้องรื้อของเดิมหนึ่งรอบ แต่ผังทั้งสองใช้เลขชุดเดียวกันตลอดไป
 *
 * เลือกอย่างหลัง ไฟล์นี้จึงรู้แค่ "โหนดมี id และมีลูก" ไม่รู้ว่าโหนดนั้นคืออะไร
 *
 * ⚠ ไฟล์นี้ต้องไม่ import อะไรที่เกี่ยวกับ React หรือ React Flow
 *   หน้าที่ของมันคือคำนวณตำแหน่งเท่านั้น การสร้างโหนดจริงเป็นเรื่องของผู้เรียก
 */

/** ผลของการวางโหนดหนึ่งโหนด */
export interface Placed<T> {
  node: T;
  /** ชั้นของผัง — 0 คือราก ใช้ทำป้ายชั้น ไม่ใช่ใช้คำนวณตำแหน่ง */
  depth: number;
  /**
   * ตำแหน่งบนแกนลึก (พิกเซล)
   *
   * ⚠ ไม่ใช่ depth * depthStep เสมอไป — ใบที่ถูกจัดเป็นคอลัมน์ (ดู stackLeaves)
   *   จะไล่ลงไปตามแกนลึกทีละ step ทั้งที่อยู่ชั้นเดียวกันหมด
   *   ผู้เรียกต้องใช้ค่านี้ตรง ๆ ห้ามคำนวณจาก depth เอง
   */
  depthPos: number;
  /** ตำแหน่งบนแกนกระจาย (พิกเซล) — พี่น้องเรียงต่อกันบนแกนนี้ */
  spreadPos: number;
  /** โหนดนี้กางลูกอยู่หรือไม่ */
  expanded: boolean;
  /** ลูกที่ถูกวาดจริง — อาจน้อยกว่าลูกทั้งหมดเมื่อมีการกรอง */
  drawnChildren: T[];
  /** ลูกทั้งหมดตามข้อมูล ไม่ว่าจะถูกวาดหรือไม่ */
  allChildren: T[];
}

/**
 * จัดใบให้เป็นคอลัมน์ใต้พ่อ แทนที่จะกางออกข้าง ๆ
 *
 * ────────────────────────────────────────────────────────────────
 *  ปัญหาที่ตัวเลือกนี้แก้
 * ────────────────────────────────────────────────────────────────
 *
 * ผังคนระดับ 9 ขึ้นไปมีลูกของปลัด 34 โหนด และ 23 โหนดในนั้นไม่มีลูกต่อ
 * (รองปลัด · ผู้ตรวจราชการ · ที่ปรึกษา · ผู้เชี่ยวชาญ) ถ้ากางออกข้างกันหมด
 * ผังจะกว้าง 9,108 พิกเซล แต่สูงแค่ 232 — พอ fitView ย่อให้พอดีจอ
 * มันจะแบนเป็นเส้นเดียวจนอ่านไม่ออกว่าเป็นต้นไม้ ซึ่งเป็นสิ่งที่ผู้ใช้เห็นจริง
 *
 * จัดใบเป็นคอลัมน์แล้วเหลือกว้างเท่าจำนวนกิ่งจริง (11 กิ่ง) สูงเท่าคอลัมน์ใบ
 * สัดส่วนใกล้ ๆ จัตุรัส ซึ่งคือรูปทรงที่ fitView ทำงานได้ดี
 *
 * ⚠ นับ "ใบ" จากลูกทั้งหมดตามข้อมูล ไม่ใช่จากลูกที่วาดอยู่
 *   โหนดที่ยุบอยู่ยังมีลูกตามข้อมูล ถ้านับจากที่วาด มันจะถูกจัดเป็นใบ
 *   แล้วพอผู้ใช้กดกาง ผังจะกระโดดเปลี่ยนรูปทั้งผืน
 */
export interface StackLeaves {
  /** ระยะห่างระหว่างใบแต่ละตัวบนแกนลึก (พิกเซล) */
  step: number;
  /** เยื้องคอลัมน์ใบออกจากแนวของพ่อเท่าไร (พิกเซล) */
  indent: number;
}

export interface LayoutOptions<T> {
  idOf: (node: T) => string;
  /** ลูกทั้งหมดของโหนดตามข้อมูล */
  childrenOf: (node: T) => T[];
  /**
   * ลูกที่จะวาดจริง — ใช้กรองตอนเจาะดูสายเดียว
   *
   * ⚠ ต้องแยกจาก childrenOf เสมอ ห้ามให้ผู้เรียกกรองมาก่อนแล้วส่งมาทางเดียว
   *   ป้าย "N หน่วยงาน" บนการ์ดต้องนับจากลูกทั้งหมด ไม่ใช่จากลูกที่วาดอยู่
   *   ถ้ารวมเป็นทางเดียว การ์ดจะบอกว่ากรมนี้มีหน่วยเดียวตอนกรองสาย ซึ่งไม่จริง
   */
  drawnChildrenOf?: (node: T, all: T[], depth: number) => T[];
  /** โหนดนี้กางอยู่หรือไม่ — ผู้เรียกเป็นคนจำสถานะกาง/ยุบเอง */
  isOpen: (node: T, depth: number) => boolean;
  /** ชั้นลึกสุดที่ยอมวาด — ชั้นสุดท้ายจะไม่กางลูกแม้ข้อมูลจะมี */
  maxDepth: number;
  /** ช่องที่โหนดหนึ่งกินบนแกนกระจาย */
  slot: number;
  /** ระยะระหว่างชั้นบนแกนลึก */
  depthStep: number;
  /** ไม่ใส่ = กางใบออกข้างเหมือนกิ่งปกติ */
  stackLeaves?: StackLeaves;
}

export interface LayoutResult<T> {
  placed: Placed<T>[];
  /** เส้นเชื่อม [id ของพ่อ, id ของลูก] */
  links: Array<[string, string]>;
}

/**
 * วางผังจากรากเดียว
 *
 * ทำงานบน "แกนลึก" (ชั้นของผัง) กับ "แกนกระจาย" (พี่น้องเรียงต่อกัน)
 * ผู้เรียกแปลงเป็น x/y เองตามทิศที่เลือก จึงใช้ได้ทั้งแนวตั้งและแนวนอน
 * ด้วยโค้ดชุดเดียว ไม่ต้องมีสองสาขาให้แก้ไม่ครบ
 *
 * ⚠ โหนดที่ยุบอยู่หรือไม่มีลูกที่วาด กินช่องเท่ากับ slot พอดี
 *   โหนดที่กางอยู่จะถูกวางไว้กึ่งกลางของกลุ่มลูก ไม่ใช่ตรงหัวแถว
 *   ถ้าวางตรงหัวแถว ผังจะเอียงไปข้างเดียวเรื่อย ๆ เมื่อกิ่งลึกขึ้น
 */
export function layoutTree<T>(root: T, opts: LayoutOptions<T>): LayoutResult<T> {
  const placed: Placed<T>[] = [];
  const links: Array<[string, string]> = [];

  function place(node: T, depth: number, start: number): { size: number; center: number } {
    /* ชั้นสุดท้ายไม่กางลูก แม้ในข้อมูลจะมีหน่วยย่อยอยู่ก็ตาม */
    const atFloor = depth >= opts.maxDepth - 1;
    const all = atFloor ? [] : opts.childrenOf(node);
    const drawn = opts.drawnChildrenOf ? opts.drawnChildrenOf(node, all, depth) : all;
    const expanded = opts.isOpen(node, depth) && drawn.length > 0;

    if (!expanded) {
      placed.push({
        node,
        depth,
        depthPos: depth * opts.depthStep,
        spreadPos: start,
        expanded: false,
        drawnChildren: [],
        allChildren: all,
      });
      return { size: opts.slot, center: start + opts.slot / 2 };
    }

    /* แยกใบออกจากกิ่ง — ใบจะถูกจัดเป็นคอลัมน์ใต้พ่อ ไม่กินความกว้าง
       ⚠ ถ้าลูกเป็นใบทั้งหมด ต้องปล่อยให้จัดคอลัมน์เหมือนกัน ไม่ใช่ย้อนกลับไปกางออกข้าง
         ไม่งั้นกรมที่มีแต่ผู้อำนวยการ (ซึ่งเป็นใบทุกตัว) จะยังกว้างเท่าเดิม */
    const stack = opts.stackLeaves;
    const leaves = stack ? drawn.filter((k) => opts.childrenOf(k).length === 0) : [];
    const branches = stack ? drawn.filter((k) => opts.childrenOf(k).length > 0) : drawn;

    /* ⚠ คอลัมน์ใบต้องกินความกว้างของตัวเองหนึ่งช่อง ไม่ใช่วางทับแนวกลางของพ่อ
       เดิมวางไว้ที่แนวกลาง ผลคือคอลัมน์ใบของรากพาดลงมากลางผัง แล้วชนกับ
       คอลัมน์ใบของกิ่งที่บังเอิญอยู่แนวเดียวกัน การ์ดทับกันจนกดใบล่างไม่ได้
       จองช่องไว้ต้นแถวแล้วให้กิ่งเริ่มถัดจากนั้น ทุกคอลัมน์จึงมีเลนของตัวเอง */
    const hasLeaves = stack !== undefined && leaves.length > 0;
    const leafLane = hasLeaves ? start : null;

    /* ⚠ เลนของคอลัมน์ใบต้องกว้าง slot + indent ไม่ใช่ slot เฉย ๆ
       ใบถูกเยื้องไปทาง indent แล้ว ถ้าจองแค่ slot กิ่งแรกจะเริ่มเร็วไป indent พิกเซล
       แล้วการ์ดใบกับการ์ดกิ่งแรกจะทับกัน — เป็นการทับแค่คู่เดียวในผังทั้งผืน
       ซึ่งมองด้วยตาแทบไม่เห็น แต่กดการ์ดที่ถูกทับไม่ได้ */
    let cursor = start + (hasLeaves && stack ? opts.slot + stack.indent : 0);
    const centers: number[] = [];
    for (const kid of branches) {
      const r = place(kid, depth + 1, cursor);
      centers.push(r.center);
      cursor += r.size;
    }

    const branchSize = Math.max(cursor - start, opts.slot);
    /* พ่ออยู่กึ่งกลางของทุกอย่างที่อยู่ใต้มัน รวมคอลัมน์ใบด้วย ไม่ใช่กึ่งกลางเฉพาะกิ่ง
       ไม่งั้นเส้นจากพ่อไปคอลัมน์ใบจะลากย้อนกลับไปทางซ้ายไกล ๆ ซึ่งอ่านว่าคนละกิ่ง */
    const firstLane = leafLane !== null ? leafLane + opts.slot / 2 : (centers[0] ?? start + branchSize / 2);
    const last = centers[centers.length - 1] ?? firstLane;
    const center = (firstLane + last) / 2;

    placed.push({
      node,
      depth,
      depthPos: depth * opts.depthStep,
      spreadPos: center - opts.slot / 2,
      expanded: true,
      drawnChildren: drawn,
      allChildren: all,
    });

    /* คอลัมน์ใบ — เยื้องจากแนวของพ่อ แล้วไล่ลงไปตามแกนลึกทีละ step */
    if (stack && leafLane !== null) {
      const base = depth * opts.depthStep + opts.depthStep;
      leaves.forEach((leaf, i) => {
        placed.push({
          node: leaf,
          depth: depth + 1,
          depthPos: base + i * stack.step,
          spreadPos: leafLane + stack.indent,
          expanded: false,
          drawnChildren: [],
          allChildren: opts.childrenOf(leaf),
        });
      });
    }

    for (const kid of drawn) links.push([opts.idOf(node), opts.idOf(kid)]);

    return { size: branchSize, center };
  }

  place(root, 0, 0);
  return { placed, links };
}
